import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Loader } from '@sunbit-dev/purchase-common/src/components/loader/Loader';
import * as purchaseStore from '~/store/reducers/purchase/purchase';
import { setPurchaseInfo } from '~/store/reducers/purchase/purchase';
import { useDispatch, useSelector } from 'react-redux';
import { ExpiredLinkPage } from '~/components/app/components/expiredLinkPage/ExpiredLinkPage';
import * as styles from './styles';
import { LandingPageProps } from './models';
import classNames from 'classnames';
import { PinCodeMethod } from '@sunbit-dev/purchase-common/src/models/PinCodeMethod';
import { loanApplicationNetworkService } from '~/services/LoanApplicationNetworkService';
import { useTrack } from '~/hooks/track';
import { EventsNames } from '~/enums/Events';
import { addError } from '~/store/reducers/errors/errors';
import { setCustomer, setIsLoggedIn } from '~/store/reducers/customer/customer';
import { ErrorCodes } from '~/enums/Errors';
import { Status } from '@sunbit-dev/purchase-common/src/enums/guided-app/Status';
import { PurchaseInfo } from '~/models/Purchase';
import { NetworkServiceError, TrackCategory } from '@sunbit-dev/purchase-common/src/services/networkService/models';
import { convertEventToError } from '@sunbit-dev/purchase-common/src/enums/utils';
import { PinCodeView } from './components/pinCodeView/PinCodeView';
import { OtpView } from './components/otpView/OtpView';
import { HealthcareTransitionView } from './components/healthcareTransitionView/HealthcareTransitionView';
import { BirthDateVerification } from '~/components/app/components/landingPage/components/birthDateVerification/BirthDateVerification';
import Customer from '@sunbit-dev/common/models/ICustomer';
import { OnRefreshFailure } from '~/services/LoanApplicationNetworkService/models';
import { PagesTestIds } from '~/enums/TestIds';
import { translationsService } from '~/services/TranslationsService';
import { setApplicationLocalStorage } from '~/helpers/utils';
import URLs, { ExternalMessagesRoutes, externalMessagesRoutes, MessageURLs } from '~/enums/URLs';
import { WelcomeView } from './components/welcomeView/WelcomeView';
import { useHistory, useLocation } from 'react-router-dom';
import qs from 'qs';
import { identifyRetailer } from '~/helpers/launchDarkly';
import { THIRD_PARTY_BOOT_TIMEOUT_MS, withTimeout } from '~/utils/withTimeout';

export const LandingPage = styled(({ className }: LandingPageProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [pinCodeMethod, setPinCodeMethod] = useState<PinCodeMethod>(PinCodeMethod.UNASSIGNED);
  const [showBirthDateValidationPopup, setShowBirthDateValidationPopup] = useState<boolean>(false);
  const [authenticationRequired, setAuthenticationRequired] = useState(false);
  const [isLinkExpired, setIsLinkExpired] = useState(false);
  const [shouldShowHealthCareTransitionView, setShouldShowHealthCareTransitionView] = useState<boolean>(false);
  const [isPhoneNumberRequired, setIsPhoneNumberRequired] = useState<boolean>(false);
  const [hasAgreedToTerms, setCustomerHasAgreedToTerms] = useState<boolean>(false);
  const [userMaskedPhoneNumber, setUserMaskedPhoneNumber] = useState<string>('');

  const { purchaseInfo } = useSelector(purchaseStore.getStore);
  const dispatch = useDispatch();
  const { track, updateIdentifier: _updateIdentifier } = useTrack();
  const { search } = useLocation();
  const history = useHistory();

  const { token } = qs.parse(search, { ignoreQueryPrefix: true });

  const onRefreshFailure: OnRefreshFailure = useCallback(async ({ error }) => {
    await dispatch(addError({ eventName: convertEventToError(EventsNames.refreshToken), payload: error }));
  }, []);

  const updateIdentifier = useCallback(
    ({ customer }) => {
      // Id can be undefined in case of prefilled initial data
      if (customer?.id) _updateIdentifier({ identifier: customer.id.toString() });
    },
    [_updateIdentifier]
  );

  const updateCustomerDetails = useCallback(
    (customer: Customer): void => {
      if (customer) {
        dispatch(setCustomer({ customer }));
        updateIdentifier({ customer });
      }
    },
    [updateIdentifier, setCustomer]
  );

  const fetchAndSetPurchaseInfo = useCallback(
    async ({ fullData }: { fullData: boolean }): Promise<Partial<PurchaseInfo>> => {
      const fetchFunctionPromise = fullData
        ? loanApplicationNetworkService.getPurchaseInfo()
        : loanApplicationNetworkService.getPurchaseInitialInfo();
      const fetchEvent = fullData ? EventsNames.getPurchaseInfo : EventsNames.getPurchaseInitialInfo;

      try {
        const purchaseInfo = await fetchFunctionPromise;
        await dispatch(setPurchaseInfo(purchaseInfo));
        const isFirstTimeLinkIsOpened = [Status.Created, Status.LinkWasSentToCustomer].includes(purchaseInfo?.status);
        if (fullData && purchaseInfo?.merchantId) {
          try {
            await withTimeout(
              identifyRetailer({
                retailerId: purchaseInfo.merchantId,
                allianceId: purchaseInfo.allianceGeneralInfo?.allianceId,
                merchantVertical: purchaseInfo.merchantVertical,
              }),
              THIRD_PARTY_BOOT_TIMEOUT_MS,
              '[LandingPage][fetchAndSetPurchaseInfo] LaunchDarkly identify timed out'
            );
          } catch (e) {
            console.error('[LandingPage][fetchAndSetPurchaseInfo] Failed to identify retailer in LaunchDarkly', e);
          }
        }

        if (purchaseInfo?.merchantVertical) {
          try {
            await withTimeout(
              translationsService.addResource({ name: purchaseInfo.merchantVertical, type: 'vertical' }),
              THIRD_PARTY_BOOT_TIMEOUT_MS,
              '[LandingPage][fetchAndSetPurchaseInfo] vertical translations timed out'
            );
          } catch (e) {
            console.error('[LandingPage][fetchAndSetPurchaseInfo] Failed to load vertical translations', e);
          }
        }
        if (isFirstTimeLinkIsOpened) loanApplicationNetworkService.updateLinkWasOpened();
        setApplicationLocalStorage(purchaseInfo);
        return purchaseInfo;
      } catch (error) {
        dispatch(addError({ eventName: fetchEvent, payload: error }));
      }
    },
    [dispatch]
  );

  const onLoginSuccess = useCallback(async () => {
    dispatch(setIsLoggedIn({ isLoggedIn: true }));
  }, [dispatch, setIsLoggedIn]);

  const fetchAndSetCustomerInfo = useCallback(async () => {
    try {
      const { customer } = await loanApplicationNetworkService.getCustomer();
      await updateCustomerDetails(customer);
      await onLoginSuccess();
    } catch (error: any) {
      if (error?.code === ErrorCodes.CustomerVerificationFlowRequired) {
        setShowBirthDateValidationPopup(true);
      } else {
        dispatch(addError({ eventName: convertEventToError(EventsNames.validateCustomerDetails), payload: error }));
      }
    }
  }, [setShowBirthDateValidationPopup, updateCustomerDetails, onLoginSuccess]);

  const isFirstTimeLogin = useCallback(async () => {
    const purchaseInfo = await fetchAndSetPurchaseInfo({ fullData: true });
    return [Status.Created, Status.LinkWasSentToCustomer, Status.LinkWasOpened].includes(purchaseInfo?.status);
  }, [fetchAndSetPurchaseInfo]);

  const onBirthDateValidationPopupClose = useCallback(
    async (customer: Customer) => {
      setShowBirthDateValidationPopup(false);
      await updateCustomerDetails(customer);
      await fetchAndSetPurchaseInfo({ fullData: true });
      await onLoginSuccess();
    },
    [setShowBirthDateValidationPopup, updateCustomerDetails, fetchAndSetPurchaseInfo, onLoginSuccess]
  );

  useEffect(() => {
    if (!isLoading) {
      track({ eventName: EventsNames.landingPage, type: TrackCategory.View });
    }
  }, [isLoading]);

  useEffect(() => {
    if (externalMessagesRoutes.includes(token as ExternalMessagesRoutes)) {
      setIsLoading(false);
      return;
    }
    const init = async () => {
      let accessToken;
      let hasExpiredLink = false;
      let hasInvalidLink = false;
      try {
        const { token } = await loanApplicationNetworkService.login({
          eventName: EventsNames.initialLogin,
          onRefreshFailure,
        });
        accessToken = token;
      } catch (error) {
        if (
          [ErrorCodes.ExpiredLink, ErrorCodes.ExpiredLinkOnPurchaseComplete].includes(
            (error as NetworkServiceError)?.responseData?.code
          )
        ) {
          setIsLinkExpired(true);
          hasExpiredLink = true;
        } else if ((error as NetworkServiceError)?.responseData?.code === ErrorCodes.ResourceNotExists) {
          hasInvalidLink = true;
          history.replace({ pathname: URLs.Message + MessageURLs.InvalidLink, search });
        } else if ((error as NetworkServiceError)?.responseData?.code === ErrorCodes.PhoneNumberRequired) {
          setAuthenticationRequired(true);
          setIsPhoneNumberRequired(true);
        } else {
          dispatch(addError({ eventName: convertEventToError(EventsNames.logIn), payload: error }));
        }
      } finally {
        const isAlreadyLoggedIn = !!accessToken;

        if (!isAlreadyLoggedIn && !hasExpiredLink && !hasInvalidLink) {
          setAuthenticationRequired(true);
          await fetchAndSetPurchaseInfo({ fullData: false });
        } else if (isAlreadyLoggedIn) {
          const isFirstLogin = await isFirstTimeLogin();
          // If he already has status navigate to the last point in the flow
          if (!isFirstLogin) {
            await fetchAndSetCustomerInfo();
          }
        }
        setIsLoading(false);
      }
    };

    init();
  }, []);

  useEffect(() => {
    if (purchaseInfo?.userMaskedPhone) {
      setUserMaskedPhoneNumber(purchaseInfo.userMaskedPhone);
    }
  }, [purchaseInfo]);

  const onPinCodeLoginSuccess = useCallback(async () => {
    try {
      const isFirstLogin = await isFirstTimeLogin();
      if (!isFirstLogin || hasAgreedToTerms) {
        await fetchAndSetCustomerInfo();
      } else {
        setAuthenticationRequired(false);
      }
    } catch (error) {
      dispatch(addError({ eventName: EventsNames.validatePinCode, payload: error }));
    }
  }, [isFirstTimeLogin, fetchAndSetCustomerInfo, hasAgreedToTerms]);

  const onPinCodeMethodSubmit = useCallback(
    (chosenPinCodeMethod: PinCodeMethod, phoneNumber: string) => {
      setPinCodeMethod(chosenPinCodeMethod);
      setCustomerHasAgreedToTerms(true);
      phoneNumber && setUserMaskedPhoneNumber(`******${phoneNumber.slice(6, 10)}`);
    },
    [setPinCodeMethod]
  );

  const onStartApplication = useCallback(async () => {
    const { amountRequested, roNumber, isHealthcareVertical } = purchaseInfo;
    if (isHealthcareVertical && amountRequested && roNumber) {
      setShouldShowHealthCareTransitionView(true);
      const minimumTransitionTime = new Promise((resolve) => setTimeout(resolve, 1500));
      await minimumTransitionTime;
    }

    await fetchAndSetCustomerInfo();
  }, [
    purchaseInfo?.status,
    purchaseInfo?.isHealthcareVertical,
    purchaseInfo?.amountRequested,
    purchaseInfo?.roNumber,
    fetchAndSetCustomerInfo,
    shouldShowHealthCareTransitionView,
  ]);

  const onResendClick = useCallback(() => {
    setPinCodeMethod(PinCodeMethod.UNASSIGNED);
  }, [setPinCodeMethod]);

  const content = (() => {
    if (isLinkExpired) {
      return <ExpiredLinkPage />;
    }
    if (authenticationRequired) {
      if (pinCodeMethod !== PinCodeMethod.UNASSIGNED) {
        return (
          <PinCodeView
            pinCodeMethod={pinCodeMethod}
            userMaskedPhoneNumber={userMaskedPhoneNumber}
            onSuccess={onPinCodeLoginSuccess}
            onResendClick={onResendClick}
            onRefreshFailure={onRefreshFailure}
          />
        );
      }
      return <OtpView onPinCodeMethodSubmit={onPinCodeMethodSubmit} isPhoneNumberRequired={isPhoneNumberRequired} />;
    }
    if (shouldShowHealthCareTransitionView) {
      return <HealthcareTransitionView amountRequested={purchaseInfo.amountRequested} />;
    }
    if (externalMessagesRoutes.includes(token as ExternalMessagesRoutes)) {
      return null;
    }
    return <WelcomeView onStartApplication={onStartApplication} />;
  })();

  if (isLoading)
    return (
      <div className={className} data-testid="loader">
        <Loader contained />
      </div>
    );

  return (
    <>
      <div
        data-testid={PagesTestIds.LandingPage}
        className={classNames(className, { 'authentication-required': authenticationRequired })}
      >
        {content}
      </div>
      {showBirthDateValidationPopup && <BirthDateVerification onClose={onBirthDateValidationPopupClose} />}
    </>
  );
})`
  ${styles.LandingPage}
`;
